import { DataProvider } from 'react-admin';
import { backendDataProvider } from './backend';
import {
    mapFbrInvoiceToUpsertBody,
    normalizeFbrInvoiceRecord,
} from '../orders/fbrInvoiceDataProvider';
import {
    mapPraInvoiceToUpsertBody,
    normalizePraInvoiceRecord,
} from '../praOrders/praInvoiceDataProvider';
import { apiFetch } from '../api/httpClient';
import { parseVehicleProfileDocumentsFromRecord } from '../workshop/vehicleProfile/vehicleProfileDocuments';
import { normalizeGlChartAccountRecord } from '../accounting/glChartAccountTransform';
import { normalizeGlJournalVoucherRecord } from '../accounting/glJournalVoucherTransform';
import { normalizeVendorRecord, transformVendorWrite } from '../vendors/vendorTransform';
import {
    normalizeVendorAccountingRecord,
    transformVendorAccountingWrite,
} from '../vendors/vendorAccountingTransform';
import {
    normalizeProductProfileRecord,
    transformProductProfileWrite,
} from '../productProfiles/productProfileTransform';

/** Prevent `filter.q.trim is not a function` when a picker passes a non-string query. */
function sanitizeListFilter(params: { filter?: unknown }): { filter?: unknown } {
    const raw = params.filter;
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return params;
    const f = { ...(raw as Record<string, unknown>) };
    if (f.q != null && typeof f.q !== 'string') {
        f.q = typeof f.q === 'number' && Number.isFinite(f.q) ? String(f.q) : '';
    }
    return { ...params, filter: f };
}

/** Ensure react-admin record id + camelCase for voucher type API payloads. */
function normalizeGlVoucherTypeRecord(row: unknown): Record<string, unknown> {
    const o =
        row && typeof row === 'object' && !Array.isArray(row)
            ? { ...(row as Record<string, unknown>) }
            : {};
    const id = o.id ?? o.Id;
    return { ...o, ...(id !== undefined ? { id } : {}) };
}

const backendResources = new Set([
    'productProfiles',
    'companies',
    'users',
    'customers',
    'vendors',
    'vendorAccounting',
    'paymentTerms',
    'shippingPolicies',
    'recurringPlans',
    'fbrProvinces',
    'fbrSaleTypes',
    'fbrRates',
    'fbrSroSchedules',
    'fbrSroItems',
    'fbrInvoices',
    'praInvoices',
    'fbrScenarios',
    'fbrSalesTaxRates',
    'glChartAccounts',
    'glAccountGroups',
    'glVoucherTypes',
    'glJournalVouchers',
    'genBankInformation',
    'genCashInformation',
    'registerCurrencies',
    'documentSequences',
    'glFiscalYears',
    'securityGroups',
    'fuelStations',
    'fuelStationDailyEntries',
    'inventWarehouses',
    'purchaseRequisitions',
    'purchaseOrders',
    'inwardGatePasses',
    'goodsReceipt',
    'purchaseInvoices',
    'purchaseReturns',
    'gateOutward',
    'vendorBills',
    'customerInvoices',
    'projectPaymentSchedules',
    'projectChangeRequests',
    'projectRoles',
    'projectCategories',
    'projectLaborTypes',
    'jobType',
    'jobsRateList',
    'maintenanceChart',
    'vehicleProfile',
    'variantInformation',
    'colorInformation',
    'customerSubParties',
    'jobEstimate',
    'estimateApproval',
    'insuranceApproval',
    'jobCard',
    'jobCardInvoice',
    'jobCardRecovery',
    'jobCardTypeBilling',
    'jobCardIssuance',
    'counterSales',
    'employee',
    'VehicleInfo',
    'stockTransactions',
    'customerPayments',
    'productCategories',
    'quotations',
    'saleOrders',
    'deliveryOrders',
    'projects',
    'dmsDocuments',
    'dmsDocumentCategories',
    'dmsDocumentTypes',
    'dmsDocumentAccessLogs',
    'salaryComponents',
    'salaryStructures',
    'employeeContracts',
    'payrollPeriods',
    'payslips',
    'taxSlabs',
    'loans',
]);

function workshopApiResource(resource: string): string {
    if (resource === 'variantInformation') return 'varientInformation';
    if (resource === 'goodsReceipt') return 'inwardGatePasses';
    if (resource === 'gateOutward') return 'outwardGatePasses';
    return resource;
}

/**
 * Legacy autodealer write endpoints used to answer with `{ message, data: {...} }`. They now return
 * the saved row directly, but unwrap defensively so a stray envelope can never resurface as
 * react-admin's "the received data does not have an 'id' key".
 */
function unwrapLegacyEnvelope(row: unknown): Record<string, unknown> {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return {};
    const o = row as Record<string, unknown>;
    const inner = o.data;
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        return { ...(inner as Record<string, unknown>) };
    }
    return { ...o };
}

function normalizeVariantRow(row: unknown): Record<string, unknown> {
    const o = unwrapLegacyEnvelope(row);
    const id = o.id ?? o.varientID ?? o.VarientID;
    return { ...o, ...(id !== undefined ? { id } : {}) };
}

function normalizeColorRow(row: unknown): Record<string, unknown> {
    const o = unwrapLegacyEnvelope(row);
    const id = o.id ?? o.colorID ?? o.ColorID;
    return { ...o, ...(id !== undefined ? { id } : {}) };
}

/** react-admin requires `id`; API may expose purchaseOrderId / PurchaseOrderID only. */
function normalizePurchaseOrderRow(row: unknown): Record<string, unknown> {
    const o =
        row && typeof row === 'object' && !Array.isArray(row)
            ? { ...(row as Record<string, unknown>) }
            : {};
    const rawId =
        o.id ?? o.purchaseOrderId ?? o.PurchaseOrderID ?? o.purchaseOrderID;
    if (rawId == null || rawId === '') return o;
    const n = typeof rawId === 'number' ? rawId : Number(rawId);
    const id = Number.isFinite(n) && n > 0 ? n : rawId;
    return { ...o, id, purchaseOrderId: o.purchaseOrderId ?? id };
}

function normalizeJobCardInvoiceRecord(row: unknown): Record<string, unknown> {
    const o =
        row && typeof row === 'object' && !Array.isArray(row)
            ? (row as Record<string, unknown>)
            : {};
    const normalizedPartsLines = Array.isArray(o.partsLines)
        ? (o.partsLines as Record<string, unknown>[]).map(line => {
              const taxIds = Array.isArray(line.fbrSalesTaxRateIds)
                  ? (line.fbrSalesTaxRateIds as unknown[]).map(x => Number(x)).filter(x => Number.isFinite(x) && x > 0)
                  : [];
              const legacy =
                  line.fbrSalesTaxRateId != null && line.fbrSalesTaxRateId !== ''
                      ? Number(line.fbrSalesTaxRateId)
                      : null;
              const merged =
                  taxIds.length > 0
                      ? taxIds
                      : legacy != null && Number.isFinite(legacy) && legacy > 0
                        ? [legacy]
                        : [];
              return { ...line, fbrSalesTaxRateIds: merged, fbrSalesTaxRateId: merged[0] ?? null };
          })
        : o.partsLines;
    return { ...o, partsLines: normalizedPartsLines };
}

function normalizeJobCardRecoveryRecord(row: unknown): Record<string, unknown> {
    const o =
        row && typeof row === 'object' && !Array.isArray(row)
            ? { ...(row as Record<string, unknown>) }
            : {};
    const rawId = o.id ?? o.customerInvoiceRecoveryId ?? o.CustomerInvoiceRecoveryId ?? o.CustomerInvoiceRecoveryID;
    if (rawId == null || rawId === '') return o;
    const n = typeof rawId === 'number' ? rawId : Number(rawId);
    const id = Number.isFinite(n) && n > 0 ? n : rawId;
    return { ...o, id, customerInvoiceRecoveryId: o.customerInvoiceRecoveryId ?? id };
}

function normalizePurchaseInvoiceRow(row: unknown): Record<string, unknown> {
    const o =
        row && typeof row === 'object' && !Array.isArray(row)
            ? { ...(row as Record<string, unknown>) }
            : {};
    const rawId = o.id ?? o.purchaseId ?? o.PurchaseID;
    if (rawId == null || rawId === '') return o;
    const n = typeof rawId === 'number' ? rawId : Number(rawId);
    const id = Number.isFinite(n) && n > 0 ? n : rawId;
    const docs = o.purchaseDocuments ?? o.purchaseDocumentsJson;
    const lines = Array.isArray(o.lines)
        ? (o.lines as Record<string, unknown>[]).map(line => {
              const taxIds = Array.isArray(line.fbrSalesTaxRateIds)
                  ? (line.fbrSalesTaxRateIds as unknown[])
                      .map(x => Number(x))
                      .filter(x => Number.isFinite(x) && x > 0)
                  : [];
              const legacy =
                  line.fbrSalesTaxRateId != null && line.fbrSalesTaxRateId !== ''
                      ? Number(line.fbrSalesTaxRateId)
                      : line.taxId != null && line.taxId !== ''
                        ? Number(line.taxId)
                        : null;
              const merged =
                  taxIds.length > 0
                      ? taxIds
                      : legacy != null && Number.isFinite(legacy) && legacy > 0
                        ? [legacy]
                        : [];
              return {
                  ...line,
                  fbrSalesTaxRateIds: merged,
                  fbrSalesTaxRateId: merged[0] ?? null,
                  taxId: merged[0] ?? null,
              };
          })
        : o.lines;
    return {
        ...o,
        id,
        purchaseId: o.purchaseId ?? id,
        purchaseDocuments: Array.isArray(docs) ? docs : o.purchaseDocuments,
        lines,
    };
}

function normalizePurchaseRequisitionRow(row: unknown): Record<string, unknown> {
    const o =
        row && typeof row === 'object' && !Array.isArray(row)
            ? { ...(row as Record<string, unknown>) }
            : {};
    const rawId = o.id ?? o.purchaseReqId ?? o.PurchaseReqID ?? o.purchaseReqID;
    if (rawId == null || rawId === '') return o;
    const n = typeof rawId === 'number' ? rawId : Number(rawId);
    const id = Number.isFinite(n) && n > 0 ? n : rawId;
    return { ...o, id, purchaseReqId: o.purchaseReqId ?? id };
}

const pdiListResources: Record<string, (params: Record<string, unknown>) => string> = {
    fbrPdiTransTypes: p => {
        const q =
            p.filter != null && typeof p.filter === 'object' && p.filter !== null && 'q' in p.filter
                ? String((p.filter as { q?: unknown }).q ?? '')
                : '';
        const pag = p.pagination as { perPage?: number } | undefined;
        const per = pag?.perPage;
        const take = typeof per === 'number' && per > 0 ? Math.min(per, 500) : 50;
        return `fbrPdi/trans-types?q=${encodeURIComponent(q)}&take=${take}`;
    },
    fbrPdiUoms: p => {
        const q = p.filter != null && typeof p.filter === 'object' && p.filter !== null && 'q' in p.filter
            ? String((p.filter as { q?: unknown }).q ?? '')
            : '';
        const pag = p.pagination as { perPage?: number } | undefined;
        const per = pag?.perPage;
        const take =
            typeof per === 'number' && per > 0 ? Math.min(per, 500) : 200;
        return `fbrPdi/uoms?q=${encodeURIComponent(q)}&take=${take}`;
    },
    fbrPdiItemDescCodes: p => {
        const q = p.filter != null && typeof p.filter === 'object' && p.filter !== null && 'q' in p.filter
            ? String((p.filter as { q?: unknown }).q ?? '')
            : '';
        const take = 50;
        return `fbrPdi/item-desc-codes?q=${encodeURIComponent(q)}&take=${take}`;
    },
    stockTransactionTypes: p => {
        const q = p.filter != null && typeof p.filter === 'object' && p.filter !== null && 'q' in p.filter
            ? String((p.filter as { q?: unknown }).q ?? '')
            : '';
        const pag = p.pagination as { perPage?: number } | undefined;
        const per = pag?.perPage;
        const take = typeof per === 'number' && per > 0 ? Math.min(per, 200) : 100;
        return `stockTransactions/types?q=${encodeURIComponent(q)}&take=${take}`;
    },
};

/** List rows: ensure grid columns bind if API ever returns PascalCase. */
function normalizeFbrInvoiceListRow(row: unknown): Record<string, unknown> {
    const o =
        row && typeof row === 'object' && !Array.isArray(row)
            ? { ...(row as Record<string, unknown>) }
            : {};
    if (o.customerAddress == null && o.CustomerAddress != null)
        o.customerAddress = o.CustomerAddress;
    if (o.customerNtn == null && o.CustomerNtn != null) o.customerNtn = o.CustomerNtn;
    if (o.customerPhone == null && o.CustomerPhone != null) o.customerPhone = o.CustomerPhone;
    if (o.customerName == null && o.CustomerName != null) o.customerName = o.CustomerName;
    return o;
}

/**
 * Some `/api/{resource}` endpoints ignore `Range` / `range` and return the full list while still
 * sending `Content-Range` with the real total. simple-rest then feeds the whole JSON array as
 * `data`, so the table shows every row while pagination shows the correct page range.
 * Slice client-side only when the payload is larger than the requested page size.
 */
function sliceListToPagination<T>(
    result: { data: T[]; total?: number },
    pagination?: { page?: number; perPage?: number }
): { data: T[]; total: number } {
    const page = pagination?.page ?? 1;
    const perPage = pagination?.perPage ?? 25;
    const data = Array.isArray(result.data) ? result.data : [];
    const resolvedTotal =
        typeof result.total === 'number' && !Number.isNaN(result.total) ? result.total : data.length;
    if (perPage <= 0 || data.length <= perPage) {
        return { data, total: resolvedTotal };
    }
    const start = Math.max(0, (page - 1) * perPage);
    return {
        data: data.slice(start, start + perPage),
        total: resolvedTotal,
    };
}

function normalizeProductCategoryRecord(row: unknown): Record<string, unknown> {
    const o =
        row && typeof row === 'object' && !Array.isArray(row)
            ? { ...(row as Record<string, unknown>) }
            : {};
    const id = o.id ?? o.productCategoryId ?? o.ProductCategoryId;
    return { ...o, ...(id !== undefined ? { id } : {}) };
}

/** react-admin requires every getMany row to have an `id` (camelCase). */
function normalizeFbrSalesTaxRateRow(row: unknown, fallbackId: string | number): Record<string, unknown> {
    const o =
        row && typeof row === 'object' && !Array.isArray(row)
            ? (row as Record<string, unknown>)
            : {};
    const id = o.id ?? o.Id ?? fallbackId;
    const percentage = o.percentage ?? o.Percentage;
    return { ...o, id, ...(percentage !== undefined ? { percentage } : {}) };
}

async function pdiGetList(pathWithQuery: string): Promise<{ data: unknown[]; total: number }> {
    const res = await apiFetch(`/api/${pathWithQuery}`, { method: 'GET' });
    let parsed: unknown;
    try {
        parsed = await res.json();
    } catch {
        parsed = null;
    }
    if (!res.ok) {
        const msg =
            parsed && typeof parsed === 'object' && parsed !== null && 'message' in parsed
                ? String((parsed as { message?: unknown }).message ?? res.statusText)
                : res.statusText;
        throw new Error(msg);
    }
    const data = Array.isArray(parsed) ? parsed : [];
    return { data, total: data.length };
}

export default (type: string) => {
    const dataProviderPromise = getDataProvider(type);

    const dataProviderWithGeneratedData = new Proxy(defaultDataProvider, {
        get(_, name) {
            if (name === 'supportAbortSignal') {
                return import.meta.env.MODE === 'production';
            }
            return (resource: string, params: any) => {
                const method = name.toString();
                const pdiBuilder = pdiListResources[resource];
                if (pdiBuilder && method === 'getList') {
                    return pdiGetList(pdiBuilder(params));
                }

                if (resource === 'fbrPdiTransTypes' && method === 'getMany') {
                    const ids = (params.ids as (string | number)[]).filter(
                        id => id !== '' && id != null
                    );
                    if (ids.length === 0) {
                        return Promise.resolve({ data: [] });
                    }
                    const idNums = new Set(ids.map(id => Number(id)));
                    return pdiGetList(
                        `fbrPdi/trans-types?q=${encodeURIComponent('')}&take=500`
                    ).then(res => {
                        const rows = res.data as Array<{ id?: number }>;
                        const data = rows.filter(
                            r => r != null && idNums.has(Number(r.id))
                        );
                        return { data };
                    });
                }

                if (resource === 'fbrPdiUoms' && method === 'getMany') {
                    const ids = (params.ids as (string | number)[]).filter(
                        id => id !== '' && id != null
                    );
                    if (ids.length === 0) {
                        return Promise.resolve({ data: [] });
                    }
                    const idNums = new Set(ids.map(id => Number(id)));
                    return pdiGetList(
                        `fbrPdi/uoms?q=${encodeURIComponent('')}&take=500`
                    ).then(res => {
                        const rows = res.data as Array<{ id?: number; uomId?: number }>;
                        const data = rows.filter(r => {
                            const rid = Number(r?.id ?? r?.uomId);
                            return rid && idNums.has(rid);
                        });
                        return { data };
                    });
                }

                if (resource === 'stockTransactionTypes' && method === 'getMany') {
                    const ids = (params.ids as (string | number)[]).filter(id => id !== '' && id != null);
                    if (ids.length === 0) return Promise.resolve({ data: [] });
                    const idNums = new Set(ids.map(id => Number(id)));
                    return pdiGetList(`stockTransactions/types?q=&take=500`).then(res => {
                        const data = (res.data as Array<{ id?: number }>).filter(r => idNums.has(Number(r?.id)));
                        return { data };
                    });
                }

                if (resource === 'variantInformation' && method === 'getMany') {
                    const ids = (params.ids as (string | number)[]).filter(
                        id => id !== '' && id != null
                    );
                    if (ids.length === 0) {
                        return Promise.resolve({ data: [] });
                    }
                    return (async () => {
                        const data: Record<string, unknown>[] = [];
                        for (const id of ids) {
                            const res = await apiFetch(
                                `/api/varientInformation/${encodeURIComponent(String(id))}`,
                                { method: 'GET' }
                            );
                            if (!res.ok) continue;
                            const row = await res.json();
                            data.push(normalizeVariantRow(row));
                        }
                        return { data };
                    })();
                }

                if (resource === 'colorInformation' && method === 'getMany') {
                    const ids = (params.ids as (string | number)[]).filter(
                        id => id !== '' && id != null
                    );
                    if (ids.length === 0) {
                        return Promise.resolve({ data: [] });
                    }
                    return (async () => {
                        const data: Record<string, unknown>[] = [];
                        for (const id of ids) {
                            const res = await apiFetch(
                                `/api/colorInformation/${encodeURIComponent(String(id))}`,
                                { method: 'GET' }
                            );
                            if (!res.ok) continue;
                            const row = await res.json();
                            data.push(normalizeColorRow(row));
                        }
                        return { data };
                    })();
                }

                if (resource === 'fbrSalesTaxRates' && method === 'getOne') {
                    // ReferenceInput used with array source calls getOne with id="1038,1048" → 404.
                    // Split and fetch each individually; return the first result.
                    const rawId = (params as { id: unknown }).id;
                    const idStr = String(rawId ?? '');
                    const splitIds = idStr.split(',').map(s => parseInt(s.trim(), 10)).filter(n => Number.isFinite(n) && n > 0);
                    if (splitIds.length === 0) return Promise.resolve({ data: { id: rawId } });
                    return (async () => {
                        const results: Record<string, unknown>[] = [];
                        for (const id of splitIds) {
                            const res = await apiFetch(
                                `/api/fbrSalesTaxRates/${encodeURIComponent(String(id))}`,
                                { method: 'GET' },
                                { auth: true, retryOn401: true }
                            );
                            const text = await res.text();
                            let row: unknown;
                            try { row = text ? JSON.parse(text) : {}; } catch { continue; }
                            if (!res.ok) continue;
                            results.push(normalizeFbrSalesTaxRateRow(row, id));
                        }
                        const first = results[0];
                        return { data: first ?? { id: rawId } };
                    })();
                }

                if (resource === 'fbrSalesTaxRates' && method === 'getMany') {
                    const ids = (params.ids as (string | number)[]).filter(
                        id => id !== '' && id != null
                    );
                    return (async () => {
                        const data: Record<string, unknown>[] = [];
                        for (const id of ids) {
                            const res = await apiFetch(
                                `/api/fbrSalesTaxRates/${encodeURIComponent(String(id))}`,
                                { method: 'GET' }
                            );
                            const text = await res.text();
                            let row: unknown;
                            try {
                                row = text ? JSON.parse(text) : {};
                            } catch {
                                continue;
                            }
                            if (!res.ok) continue;
                            data.push(normalizeFbrSalesTaxRateRow(row, id));
                        }
                        return { data };
                    })();
                }

                if (resource === 'fbrSalesTaxRates' && method === 'getList') {
                    const filt =
                        params.filter && typeof params.filter === 'object' && params.filter !== null
                            ? (params.filter as Record<string, unknown>)
                            : {};
                    const rawAsOf = filt.asOf;
                    const asOf =
                        rawAsOf instanceof Date && !Number.isNaN(rawAsOf.getTime())
                            ? rawAsOf.toISOString().slice(0, 10)
                            : typeof rawAsOf === 'string' && rawAsOf.trim() !== ''
                              ? rawAsOf.trim().slice(0, 10)
                              : '';
                    const qRaw = filt.q;
                    const q =
                        typeof qRaw === 'string' && qRaw.trim() !== ''
                            ? qRaw.trim()
                            : typeof qRaw === 'number'
                              ? String(qRaw)
                              : '';
                    const qsParts: string[] = [];
                    if (asOf) qsParts.push(`asOf=${encodeURIComponent(asOf)}`);
                    if (q) qsParts.push(`filter=${encodeURIComponent(JSON.stringify({ q }))}`);
                    const qs = qsParts.length > 0 ? `?${qsParts.join('&')}` : '';
                    return (async () => {
                        const res = await apiFetch(`/api/fbrSalesTaxRates${qs}`, { method: 'GET' });
                        const text = await res.text();
                        let parsed: unknown;
                        try {
                            parsed = text ? JSON.parse(text) : [];
                        } catch {
                            parsed = [];
                        }
                        if (!res.ok) {
                            const msg =
                                parsed &&
                                typeof parsed === 'object' &&
                                parsed !== null &&
                                'message' in parsed
                                    ? String((parsed as { message?: unknown }).message ?? res.statusText)
                                    : res.statusText;
                            throw new Error(msg);
                        }
                        const rawList = Array.isArray(parsed) ? parsed : [];
                        const data = rawList.map((row, i) => {
                            const o =
                                row && typeof row === 'object' && !Array.isArray(row)
                                    ? (row as Record<string, unknown>)
                                    : {};
                            const fallbackId = (o.id ?? o.Id ?? i) as string | number;
                            return normalizeFbrSalesTaxRateRow(row, fallbackId);
                        });
                        const cr = res.headers.get('Content-Range') ?? '';
                        let total = data.length;
                        const slash = cr.lastIndexOf('/');
                        if (slash >= 0) {
                            const n = parseInt(cr.slice(slash + 1), 10);
                            if (!Number.isNaN(n)) total = n;
                        }
                        return { data, total };
                    })();
                }

                if (resource === 'workshopFloor' && method === 'getList') {
                    const page = params.pagination?.page ?? 1;
                    const perPage = params.pagination?.perPage ?? 50;
                    const start = Math.max(0, (page - 1) * perPage);
                    const end = start + perPage - 1;
                    const sortField = params.sort?.field ?? 'jobCardId';
                    const sortOrder = params.sort?.order ?? 'DESC';
                    const filterObj: Record<string, unknown> = {};
                    const f = params.filter as Record<string, unknown> | undefined;
                    if (f?.q != null && String(f.q).trim()) filterObj.q = String(f.q).trim();
                    if (f?.approvalStatusCode != null)
                        filterObj.approvalStatusCode = String(f.approvalStatusCode).trim();
                    if (f?.regNo != null && String(f.regNo).trim()) filterObj.regNo = String(f.regNo).trim();
                    if (f?.chasisNo != null && String(f.chasisNo).trim()) filterObj.chasisNo = String(f.chasisNo).trim();
                    const qs = new URLSearchParams({
                        range: `${start}-${end}`,
                        sort: String(sortField),
                        order: String(sortOrder),
                        filter: JSON.stringify(filterObj),
                    });
                    return apiFetch(`/api/jobCard/floor?${qs}`, { method: 'GET' }, { auth: true, retryOn401: true }).then(
                        async res => {
                            if (!res.ok) {
                                const err = new Error(res.statusText || 'Floor list failed') as Error & {
                                    status?: number;
                                };
                                err.status = res.status;
                                throw err;
                            }
                            const rows = (await res.json()) as unknown[];
                            const data = (Array.isArray(rows) ? rows : []).map(row => {
                                const o =
                                    row && typeof row === 'object' && !Array.isArray(row)
                                        ? { ...(row as Record<string, unknown>) }
                                        : {};
                                const id = o.id ?? o.jobCardId ?? o.JobCardId;
                                return { ...o, ...(id !== undefined ? { id } : {}) };
                            });
                            const cr = res.headers.get('Content-Range') ?? '';
                            let total = data.length;
                            const slash = cr.lastIndexOf('/');
                            if (slash >= 0) {
                                const n = parseInt(cr.slice(slash + 1), 10);
                                if (!Number.isNaN(n)) total = n;
                            }
                            return { data, total };
                        }
                    );
                }
                if (resource === 'workshopFloor') {
                    const err = new Error('Workshop Floor is read-only; use floor actions from the list.') as Error & {
                        status?: number;
                    };
                    err.status = 405;
                    return Promise.reject(err);
                }

                // Workshop GL defaults — handled before backendResources gate (not in that set)
                if (resource === 'workshopGlDefaults') {
                    if (method === 'getOne') {
                        return apiFetch('/api/vehicleSaleGlCompanyDefaults', { method: 'GET' }, { auth: true, retryOn401: true })
                            .then(async res => {
                                const j = await res.json().catch(() => ({})) as { data?: unknown };
                                const d = (j.data ?? j) as Record<string, unknown>;
                                return { data: { ...d, id: 0 } };
                            });
                    }
                    if (method === 'update') {
                        return apiFetch(
                            '/api/vehicleSaleGlCompanyDefaults/workshop',
                            { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify((params as { data: unknown }).data) },
                            { auth: true, retryOn401: true }
                        ).then(async res => {
                            const j = await res.json().catch(() => ({})) as { data?: unknown };
                            const d = (j.data ?? j) as Record<string, unknown>;
                            return { data: { ...d, id: 0 } };
                        });
                    }
                    return Promise.resolve({ data: { id: 0 } });
                }

                // Project tasks — react-admin resource "tasks" adapted onto the custom
                // /api/projectWbs/tasks endpoints (the DTO already carries `id = TaskID`).
                if (resource === 'tasks') {
                    if (method === 'getList') {
                        return (async () => {
                            const res = await apiFetch('/api/projectWbs/tasks/all', { method: 'GET' }, { auth: true, retryOn401: true });
                            const rows = res.ok ? ((await res.json().catch(() => [])) as Record<string, unknown>[]) : [];
                            let data = Array.isArray(rows) ? rows.slice() : [];

                            const f = (params.filter ?? {}) as Record<string, unknown>;
                            const q = typeof f.q === 'string' ? f.q.trim().toLowerCase() : '';
                            if (q) {
                                data = data.filter(r =>
                                    `${r.taskTitle ?? ''} ${r.projectName ?? ''} ${r.status ?? ''}`.toLowerCase().includes(q)
                                );
                            }
                            if (f.projectId != null && f.projectId !== '') {
                                const pid = Number(f.projectId);
                                data = data.filter(r => Number(r.projectId) === pid);
                            }

                            const sort = params.sort as { field?: string; order?: string } | undefined;
                            const field = sort?.field ?? 'taskId';
                            const order = (sort?.order ?? 'DESC').toUpperCase() === 'ASC' ? 1 : -1;
                            data.sort((a, b) => {
                                const av = a[field] as unknown;
                                const bv = b[field] as unknown;
                                if (av == null && bv == null) return 0;
                                if (av == null) return 1;
                                if (bv == null) return -1;
                                if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * order;
                                return String(av).localeCompare(String(bv)) * order;
                            });
                            return sliceListToPagination({ data, total: data.length }, params.pagination);
                        })();
                    }
                    if (method === 'getOne') {
                        return (async () => {
                            const res = await apiFetch(
                                `/api/projectWbs/tasks/${encodeURIComponent(String(params.id))}`,
                                { method: 'GET' },
                                { auth: true, retryOn401: true }
                            );
                            if (!res.ok) {
                                const err = new Error(res.statusText || 'Task not found') as Error & { status?: number };
                                err.status = res.status;
                                throw err;
                            }
                            const data = (await res.json()) as Record<string, unknown>;
                            return { data };
                        })();
                    }
                    if (method === 'getMany') {
                        const ids = ((params.ids as (string | number)[]) ?? []).filter(id => id != null && id !== '');
                        if (ids.length === 0) return Promise.resolve({ data: [] });
                        const idSet = new Set(ids.map(id => Number(id)));
                        return (async () => {
                            const res = await apiFetch('/api/projectWbs/tasks/all', { method: 'GET' }, { auth: true, retryOn401: true });
                            const rows = res.ok ? ((await res.json().catch(() => [])) as Record<string, unknown>[]) : [];
                            const data = (Array.isArray(rows) ? rows : []).filter(r => idSet.has(Number(r.id ?? r.taskId)));
                            return { data };
                        })();
                    }
                    if (method === 'create') {
                        return (async () => {
                            const res = await apiFetch('/api/projectWbs/tasks', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify((params as { data: unknown }).data),
                            });
                            const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
                            if (!res.ok) throw new Error(String(json.message ?? res.statusText ?? 'Save failed'));
                            return { data: json };
                        })();
                    }
                    if (method === 'update') {
                        return (async () => {
                            const res = await apiFetch(
                                `/api/projectWbs/tasks/${encodeURIComponent(String(params.id))}`,
                                {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify((params as { data: unknown }).data),
                                }
                            );
                            const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
                            if (!res.ok) throw new Error(String(json.message ?? res.statusText ?? 'Save failed'));
                            return { data: json };
                        })();
                    }
                    if (method === 'delete') {
                        return (async () => {
                            const res = await apiFetch(
                                `/api/projectWbs/tasks/${encodeURIComponent(String(params.id))}`,
                                { method: 'DELETE' },
                                { auth: true, retryOn401: true }
                            );
                            const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
                            if (!res.ok) throw new Error(String(json.message ?? res.statusText ?? 'Delete failed'));
                            return { data: { id: params.id } };
                        })();
                    }
                    return Promise.resolve({ data: {} });
                }

                if (backendResources.has(resource)) {
                    if (resource === 'fbrInvoices' && method === 'getList') {
                        return backendDataProvider
                            .getList(resource, params)
                            .then(result =>
                                sliceListToPagination(
                                    {
                                        ...result,
                                        data: (result.data as unknown[]).map(normalizeFbrInvoiceListRow),
                                    },
                                    params.pagination
                                )
                            );
                    }
                    if (resource === 'praInvoices' && method === 'getList') {
                        return backendDataProvider
                            .getList(resource, params)
                            .then(result => sliceListToPagination(result, params.pagination));
                    }
                    if (resource === 'glChartAccounts' && method === 'getList') {
                        return backendDataProvider
                            .getList(resource, sanitizeListFilter(params))
                            .then(result => sliceListToPagination(result, params.pagination));
                    }
                    // getMany via filter={"id":[...]} is not supported by the backend; use individual getOne calls
                    if (resource === 'glChartAccounts' && method === 'getMany') {
                        const ids = (params as { ids: (string | number)[] }).ids ?? [];
                        if (ids.length === 0) return Promise.resolve({ data: [] });
                        return Promise.all(
                            ids.map(id =>
                                backendDataProvider.getOne(resource, { id })
                                    .then(r => normalizeGlChartAccountRecord(r.data as Record<string, unknown>))
                                    .catch(() => null)
                            )
                        ).then(results => ({ data: results.filter(Boolean) as Record<string, unknown>[] }));
                    }
                    if (resource === 'glVoucherTypes' && method === 'getMany') {
                        const ids = (params as { ids: (string | number)[] }).ids ?? [];
                        if (ids.length === 0) return Promise.resolve({ data: [] });
                        return Promise.all(
                            ids.map(id =>
                                backendDataProvider.getOne(resource, { id })
                                    .then(r => normalizeGlVoucherTypeRecord(r.data as Record<string, unknown>))
                                    .catch(() => null)
                            )
                        ).then(results => ({ data: results.filter(Boolean) as Record<string, unknown>[] }));
                    }
                    if (
                        resource === 'glVoucherTypes' &&
                        (method === 'getOne' || method === 'create' || method === 'update')
                    ) {
                        return (
                            backendDataProvider as Record<
                                string,
                                (r: string, p: unknown) => Promise<{ data: unknown }>
                            >
                        )[method](resource, params).then(result => ({
                            data: normalizeGlVoucherTypeRecord(result.data as Record<string, unknown>),
                        }));
                    }
                    if (
                        resource === 'glChartAccounts' &&
                        (method === 'getOne' || method === 'create' || method === 'update')
                    ) {
                        return (
                            backendDataProvider as Record<
                                string,
                                (r: string, p: unknown) => Promise<{ data: unknown }>
                            >
                        )[method](resource, params).then(result => ({
                            data: normalizeGlChartAccountRecord(result.data as Record<string, unknown>),
                        }));
                    }
                    if (
                        resource === 'glJournalVouchers' &&
                        (method === 'getOne' || method === 'create' || method === 'update')
                    ) {
                        return (
                            backendDataProvider as Record<
                                string,
                                (r: string, p: unknown) => Promise<{ data: unknown }>
                            >
                        )[method](resource, params).then(result => ({
                            data: normalizeGlJournalVoucherRecord(result.data as Record<string, unknown>),
                        }));
                    }
                    // controlAccountTxnNature lives on glVoucherTypes, not glJournalVouchers,
                    // so the journal-voucher list API ignores it as a filter.
                    // Fix: Step 1 — fetch matching voucher-type rows to get their IDs and titles.
                    //      Step 2 — fetch journal vouchers filtered by systemType (server-side),
                    //               then narrow client-side via voucherTypeId OR journalTitle.
                    // journalTitle IS present in every list-API row (it is the joined voucher-type
                    // title), so it is the reliable fallback when voucherTypeId is absent.
                    if (resource === 'glJournalVouchers' && method === 'getList') {
                        const filterObj = ((params as { filter?: unknown }).filter ?? {}) as Record<string, unknown>;
                        const controlNature = filterObj.controlAccountTxnNature;
                        const hasControlNatureFilter = controlNature != null && controlNature !== '';

                        if (!hasControlNatureFilter) {
                            return backendDataProvider
                                .getList(resource, params)
                                .then(result =>
                                    sliceListToPagination(
                                        result,
                                        (params as { pagination?: { page?: number; perPage?: number } }).pagination
                                    )
                                );
                        }

                        // Step 1 — resolve voucher types that match {systemType, controlAccountTxnNature}
                        const vtFilter: Record<string, unknown> = { controlAccountTxnNature: controlNature };
                        if (filterObj.systemType != null) vtFilter.systemType = filterObj.systemType;

                        return backendDataProvider
                            .getList('glVoucherTypes', {
                                filter: vtFilter,
                                sort: { field: 'id', order: 'ASC' },
                                pagination: { page: 1, perPage: 500 },
                            })
                            .then(vtResult => {
                                const vtRows = vtResult.data as Record<string, unknown>[];

                                if (vtRows.length === 0) {
                                    // No voucher types configured for this category → genuinely empty
                                    return { data: [] as unknown[], total: 0 };
                                }

                                // Collect IDs (all possible field-name variants the API might use)
                                const validTypeIds = new Set(
                                    vtRows
                                        .map(vt => Number(
                                            vt.id ?? vt.Id ?? vt.Voucherid ?? vt.voucherId ?? vt.VoucherId ?? 0
                                        ))
                                        .filter(n => n > 0)
                                );

                                // Collect titles — journalTitle in the list response equals
                                // the voucher type's title field, so title-matching is exact.
                                const validTitles = new Set(
                                    vtRows
                                        .map(vt => String(vt.title ?? vt.Title ?? '').trim().toLowerCase())
                                        .filter(s => s.length > 0)
                                );

                                // Step 2 — fetch journal vouchers pre-filtered by systemType (supported
                                // server-side); drop controlAccountTxnNature which the API ignores.
                                const upstreamFilter: Record<string, unknown> = {};
                                for (const [k, v] of Object.entries(filterObj)) {
                                    if (k !== 'controlAccountTxnNature') upstreamFilter[k] = v;
                                }

                                return backendDataProvider
                                    .getList(resource, {
                                        ...(params as object),
                                        filter: upstreamFilter,
                                        pagination: { page: 1, perPage: 9999 },
                                    })
                                    .then(jvResult => {
                                        const filtered = (jvResult.data as Record<string, unknown>[]).filter(row => {
                                            // Primary: match by voucherTypeId FK (various possible field names)
                                            const vtId = Number(
                                                row.voucherTypeId
                                                ?? row.VoucherTypeId
                                                ?? row.vType
                                                ?? row.VType
                                                ?? 0
                                            );
                                            if (vtId > 0 && validTypeIds.has(vtId)) return true;

                                            // Fallback: match by journalTitle (always present in list response)
                                            const jTitle = String(
                                                row.journalTitle ?? row.JournalTitle ?? ''
                                            ).trim().toLowerCase();
                                            return jTitle.length > 0 && validTitles.has(jTitle);
                                        });
                                        return sliceListToPagination(
                                            { data: filtered, total: filtered.length },
                                            (params as { pagination?: { page?: number; perPage?: number } }).pagination
                                        );
                                    });
                            });
                    }
                    if (resource === 'fbrInvoices' && method === 'getOne') {
                        return backendDataProvider
                            .getOne(resource, params)
                            .then(result => ({
                                data: normalizeFbrInvoiceRecord(
                                    result.data as Record<string, unknown>
                                ),
                            }));
                    }
                    if (resource === 'praInvoices' && method === 'getOne') {
                        return backendDataProvider
                            .getOne(resource, params)
                            .then(result => ({
                                data: normalizePraInvoiceRecord(
                                    result.data as Record<string, unknown>
                                ),
                            }));
                    }
                    if (resource === 'jobsRateList' && (method === 'update' || method === 'create' || method === 'delete')) {
                        const err = new Error(
                            'Jobs Rate List uses the rate API; save from the form toolbar.'
                        ) as Error & { status?: number };
                        err.status = 405;
                        return Promise.reject(err);
                    }
                    if (resource === 'jobsRateList' && method === 'getOne') {
                        const p = params as { id: string | number; meta?: { branchId?: number } };
                        const branchQ =
                            p.meta?.branchId != null && p.meta.branchId > 0
                                ? `?branchId=${encodeURIComponent(String(p.meta.branchId))}`
                                : '';
                        return apiFetch(
                            `/api/jobsRateList/${encodeURIComponent(String(p.id))}${branchQ}`,
                            { method: 'GET' },
                            { auth: true, retryOn401: true }
                        ).then(async res => {
                            if (!res.ok) {
                                const err = new Error(res.statusText || 'Not found') as Error & {
                                    status?: number;
                                };
                                err.status = res.status;
                                throw err;
                            }
                            const data = (await res.json()) as Record<string, unknown>;
                            return { data: { ...data, id: data.id ?? data.jobInfoId } };
                        });
                    }
                    if (resource === 'fbrInvoices' && method === 'create') {
                        const data = mapFbrInvoiceToUpsertBody(params.data);
                        return backendDataProvider
                            .create(resource, { ...params, data })
                            .then(result => ({
                                data: normalizeFbrInvoiceRecord(
                                    result.data as Record<string, unknown>
                                ),
                            }));
                    }
                    if (resource === 'fbrInvoices' && method === 'update') {
                        const data = mapFbrInvoiceToUpsertBody(params.data);
                        return backendDataProvider
                            .update(resource, { ...params, data })
                            .then(result => ({
                                data: normalizeFbrInvoiceRecord(
                                    result.data as Record<string, unknown>
                                ),
                            }));
                    }
                    if (resource === 'praInvoices' && method === 'create') {
                        const data = mapPraInvoiceToUpsertBody(params.data);
                        return backendDataProvider
                            .create(resource, { ...params, data })
                            .then(result => ({
                                data: normalizePraInvoiceRecord(
                                    result.data as Record<string, unknown>
                                ),
                            }));
                    }
                    if (resource === 'praInvoices' && method === 'update') {
                        const data = mapPraInvoiceToUpsertBody(params.data);
                        return backendDataProvider
                            .update(resource, { ...params, data })
                            .then(result => ({
                                data: normalizePraInvoiceRecord(
                                    result.data as Record<string, unknown>
                                ),
                            }));
                    }
                    if (method === 'getList') {
                        const apiResource = workshopApiResource(resource);
                        return backendDataProvider
                            .getList(apiResource, params)
                            .then(result => {
                                if (resource === 'variantInformation') {
                                    return sliceListToPagination(
                                        {
                                            ...result,
                                            data: (result.data as unknown[]).map(normalizeVariantRow),
                                        },
                                        params.pagination
                                    );
                                }
                                if (resource === 'colorInformation') {
                                    return sliceListToPagination(
                                        {
                                            ...result,
                                            data: (result.data as unknown[]).map(normalizeColorRow),
                                        },
                                        params.pagination
                                    );
                                }
                                if (resource === 'purchaseOrders') {
                                    return sliceListToPagination(
                                        {
                                            ...result,
                                            data: (result.data as unknown[]).map(normalizePurchaseOrderRow),
                                        },
                                        params.pagination
                                    );
                                }
                                if (resource === 'purchaseRequisitions') {
                                    return sliceListToPagination(
                                        {
                                            ...result,
                                            data: (result.data as unknown[]).map(normalizePurchaseRequisitionRow),
                                        },
                                        params.pagination
                                    );
                                }
                                if (resource === 'purchaseInvoices') {
                                    return sliceListToPagination(
                                        {
                                            ...result,
                                            data: (result.data as unknown[]).map(normalizePurchaseInvoiceRow),
                                        },
                                        params.pagination
                                    );
                                }
                                if (resource === 'vendors') {
                                    return sliceListToPagination(
                                        {
                                            ...result,
                                            data: (result.data as unknown[]).map(normalizeVendorRecord),
                                        },
                                        params.pagination
                                    );
                                }
                                if (resource === 'jobCardRecovery') {
                                    return sliceListToPagination(
                                        {
                                            ...result,
                                            data: (result.data as unknown[]).map(normalizeJobCardRecoveryRecord),
                                        },
                                        params.pagination
                                    );
                                }
                                if (resource === 'productCategories') {
                                    return sliceListToPagination(
                                        {
                                            ...result,
                                            data: (result.data as unknown[]).map(normalizeProductCategoryRecord),
                                        },
                                        params.pagination
                                    );
                                }
                                return sliceListToPagination(result, params.pagination);
                            });
                    }
                    if (resource === 'jobCardRecovery' && (method === 'getOne' || method === 'create' || method === 'update')) {
                        return (
                            backendDataProvider as Record<
                                string,
                                (r: string, p: unknown) => Promise<{ data: unknown }>
                            >
                        )[method](resource, params).then(result => ({
                            data: normalizeJobCardRecoveryRecord(result.data as Record<string, unknown>),
                        }));
                    }

                    if (resource === 'jobCardInvoice' && (method === 'getOne' || method === 'create' || method === 'update')) {
                        const apiResource = workshopApiResource(resource);
                        return (
                            backendDataProvider as Record<
                                string,
                                (r: string, p: unknown) => Promise<{ data: unknown }>
                            >
                        )[method](apiResource, params).then(result => ({
                            data: normalizeJobCardInvoiceRecord(result.data as Record<string, unknown>),
                        }));
                    }
                    if (resource === 'purchaseOrders' && method === 'getOne') {
                        return backendDataProvider.getOne(resource, params).then(result => ({
                            data: normalizePurchaseOrderRow(result.data),
                        }));
                    }
                    if (resource === 'purchaseRequisitions' && method === 'getOne') {
                        return backendDataProvider.getOne(resource, params).then(result => ({
                            data: normalizePurchaseRequisitionRow(result.data),
                        }));
                    }
                    if (resource === 'purchaseInvoices' && method === 'getOne') {
                        return backendDataProvider.getOne(resource, params).then(result => ({
                            data: normalizePurchaseInvoiceRow(result.data),
                        }));
                    }
                    // Per-id getOne so employee-linked parties hidden from the picker list
                    // still hydrate on historical documents.
                    if (resource === 'customers' && method === 'getMany') {
                        const ids = (params as { ids: (string | number)[] }).ids ?? [];
                        if (ids.length === 0) return Promise.resolve({ data: [] });
                        return Promise.all(
                            ids.map(id =>
                                backendDataProvider.getOne(resource, { id })
                                    .then(r => r.data as Record<string, unknown>)
                                    .catch(() => null)
                            )
                        ).then(results => ({ data: results.filter(Boolean) as Record<string, unknown>[] }));
                    }
                    if (resource === 'vendors' && method === 'getMany') {
                        const ids = (params as { ids: (string | number)[] }).ids ?? [];
                        if (ids.length === 0) return Promise.resolve({ data: [] });
                        return Promise.all(
                            ids.map(id =>
                                backendDataProvider.getOne(resource, { id })
                                    .then(r => normalizeVendorRecord(r.data))
                                    .catch(() => null)
                            )
                        ).then(results => ({ data: results.filter(Boolean) as Record<string, unknown>[] }));
                    }
                    if (resource === 'vendors' && method === 'getOne') {
                        return backendDataProvider.getOne(resource, params).then(result => ({
                            data: normalizeVendorRecord(result.data),
                        }));
                    }
                    if (resource === 'productProfiles' && method === 'getOne') {
                        return backendDataProvider.getOne(resource, params).then(result => ({
                            data: normalizeProductProfileRecord(result.data),
                        }));
                    }
                    if (
                        resource === 'productProfiles' &&
                        (method === 'create' || method === 'update')
                    ) {
                        const data = transformProductProfileWrite(
                            params.data as Record<string, unknown>
                        );
                        return (
                            backendDataProvider as Record<
                                string,
                                (r: string, p: unknown) => Promise<{ data: unknown }>
                            >
                        )[method](resource, { ...params, data }).then(result => ({
                            data: normalizeProductProfileRecord(result.data),
                        }));
                    }
                    if (resource === 'vendorAccounting' && method === 'getOne') {
                        return (async () => {
                            const res = await apiFetch(
                                `/api/vendors/${encodeURIComponent(String(params.id))}/accounting`,
                                { method: 'GET' }
                            );
                            const text = await res.text();
                            let parsed: unknown;
                            try {
                                parsed = text ? JSON.parse(text) : {};
                            } catch {
                                parsed = {};
                            }
                            if (!res.ok) {
                                const msg =
                                    parsed &&
                                    typeof parsed === 'object' &&
                                    parsed !== null &&
                                    'message' in parsed
                                        ? String((parsed as { message?: unknown }).message ?? res.statusText)
                                        : res.statusText;
                                throw new Error(msg);
                            }
                            const envelope =
                                parsed && typeof parsed === 'object' && parsed !== null && 'data' in parsed
                                    ? (parsed as { data: unknown }).data
                                    : parsed;
                            return {
                                data: normalizeVendorAccountingRecord(envelope),
                            };
                        })();
                    }
                    if (resource === 'vendorAccounting' && method === 'update') {
                        return (async () => {
                            const data = transformVendorAccountingWrite(
                                params.data as Record<string, unknown>
                            );
                            const res = await apiFetch(
                                `/api/vendors/${encodeURIComponent(String(params.id))}/accounting`,
                                {
                                    method: 'PUT',
                                    body: JSON.stringify(data),
                                }
                            );
                            const text = await res.text();
                            let parsed: unknown;
                            try {
                                parsed = text ? JSON.parse(text) : {};
                            } catch {
                                parsed = {};
                            }
                            if (!res.ok) {
                                const msg =
                                    parsed &&
                                    typeof parsed === 'object' &&
                                    parsed !== null &&
                                    'message' in parsed
                                        ? String((parsed as { message?: unknown }).message ?? res.statusText)
                                        : res.statusText;
                                throw new Error(msg);
                            }
                            const envelope =
                                parsed && typeof parsed === 'object' && parsed !== null && 'data' in parsed
                                    ? (parsed as { data: unknown }).data
                                    : parsed;
                            return {
                                data: normalizeVendorAccountingRecord(envelope),
                            };
                        })();
                    }
                    if (
                        resource === 'purchaseOrders' &&
                        (method === 'create' || method === 'update')
                    ) {
                        return (
                            backendDataProvider as Record<
                                string,
                                (r: string, p: unknown) => Promise<{ data: unknown }>
                            >
                        )[method](resource, params).then(result => ({
                            data: normalizePurchaseOrderRow(result.data),
                        }));
                    }
                    if (
                        resource === 'purchaseRequisitions' &&
                        (method === 'create' || method === 'update')
                    ) {
                        return (
                            backendDataProvider as Record<
                                string,
                                (r: string, p: unknown) => Promise<{ data: unknown }>
                            >
                        )[method](resource, params).then(result => ({
                            data: normalizePurchaseRequisitionRow(result.data),
                        }));
                    }
                    if (
                        resource === 'purchaseInvoices' &&
                        (method === 'create' || method === 'update')
                    ) {
                        return (
                            backendDataProvider as Record<
                                string,
                                (r: string, p: unknown) => Promise<{ data: unknown }>
                            >
                        )[method](resource, params).then(result => ({
                            data: normalizePurchaseInvoiceRow(result.data),
                        }));
                    }
                    if (resource === 'vendors' && (method === 'create' || method === 'update')) {
                        const data =
                            params.data && typeof params.data === 'object'
                                ? transformVendorWrite(params.data as Record<string, unknown>)
                                : params.data;
                        return (
                            backendDataProvider as Record<
                                string,
                                (r: string, p: unknown) => Promise<{ data: unknown }>
                            >
                        )[method](resource, { ...params, data }).then(result => ({
                            data: normalizeVendorRecord(result.data),
                        }));
                    }
                    const apiResource = workshopApiResource(resource);
                    if (resource === 'variantInformation' && (method === 'getOne' || method === 'create' || method === 'update')) {
                        return (
                            backendDataProvider as Record<
                                string,
                                (r: string, p: unknown) => Promise<{ data: unknown }>
                            >
                        )[method](apiResource, params).then(result => ({
                            data: normalizeVariantRow(result.data),
                        }));
                    }
                    if (resource === 'colorInformation' && (method === 'getOne' || method === 'create' || method === 'update')) {
                        return (
                            backendDataProvider as Record<
                                string,
                                (r: string, p: unknown) => Promise<{ data: unknown }>
                            >
                        )[method](apiResource, params).then(result => ({
                            data: normalizeColorRow(result.data),
                        }));
                    }
                    if (resource === 'vehicleProfile' && method === 'getOne') {
                        return backendDataProvider.getOne(apiResource, params).then(result => {
                            const record =
                                result.data && typeof result.data === 'object'
                                    ? (result.data as Record<string, unknown>)
                                    : {};
                            return {
                                data: {
                                    ...record,
                                    vehicleProfileDocuments: parseVehicleProfileDocumentsFromRecord(record),
                                },
                            };
                        });
                    }
                    if (
                        resource === 'productCategories' &&
                        (method === 'getOne' || method === 'create' || method === 'update')
                    ) {
                        return (
                            backendDataProvider as Record<
                                string,
                                (r: string, p: unknown) => Promise<{ data: unknown }>
                            >
                        )[method](resource, params).then(result => ({
                            data: normalizeProductCategoryRecord(result.data as Record<string, unknown>),
                        }));
                    }
                    // documentSequences uses a pipe-delimited composite ID ("companyId|DocumentType").
                    // ra-data-simple-rest does not URL-encode the id, so | passes raw and may confuse
                    // routing layers. Encode it explicitly here for getOne and update.
                    if (resource === 'documentSequences' && method === 'getOne') {
                        const rawId = String((params as { id: unknown }).id ?? '');
                        return apiFetch(
                            `/api/documentSequences/${encodeURIComponent(rawId)}`,
                            { method: 'GET' },
                            { auth: true, retryOn401: true }
                        ).then(async res => {
                            const text = await res.text();
                            let json: unknown;
                            try { json = text ? JSON.parse(text) : {}; } catch { json = {}; }
                            if (!res.ok) {
                                const msg =
                                    typeof json === 'object' && json !== null && 'message' in json
                                        ? String((json as { message?: unknown }).message ?? res.statusText)
                                        : res.statusText;
                                throw new Error(msg);
                            }
                            return { data: json as Record<string, unknown> };
                        });
                    }
                    if (resource === 'documentSequences' && method === 'update') {
                        const rawId = String((params as { id: unknown }).id ?? '');
                        const body = (params as { data: unknown }).data;
                        return apiFetch(
                            `/api/documentSequences/${encodeURIComponent(rawId)}`,
                            { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
                            { auth: true, retryOn401: true }
                        ).then(async res => {
                            const text = await res.text();
                            let json: unknown;
                            try { json = text ? JSON.parse(text) : {}; } catch { json = {}; }
                            if (!res.ok) {
                                const msg =
                                    typeof json === 'object' && json !== null && 'message' in json
                                        ? String((json as { message?: unknown }).message ?? res.statusText)
                                        : res.statusText;
                                throw new Error(msg);
                            }
                            return { data: json as Record<string, unknown> };
                        });
                    }
                    return (backendDataProvider as Record<string, (r: string, p: unknown) => Promise<unknown>>)[method](
                        apiResource,
                        params
                    );
                }

                return dataProviderPromise.then(dataProvider =>
                    dataProvider[name.toString()](resource, params)
                );
            };
        },
    });

    return dataProviderWithGeneratedData;
};

const getDataProvider = async (type: string): Promise<DataProvider> => {
    if (type === 'graphql') {
        return import('./graphql').then(factory => factory.default());
    }
    return import('./rest').then(provider => provider.default);
};

const defaultDataProvider: DataProvider = {
    // @ts-ignore
    create: () => Promise.resolve({ data: { id: 0 } }),
    // @ts-ignore
    delete: () => Promise.resolve({ data: {} }),
    deleteMany: () => Promise.resolve({}),
    getList: () => Promise.resolve({ data: [], total: 0 }),
    getMany: () => Promise.resolve({ data: [] }),
    getManyReference: () => Promise.resolve({ data: [], total: 0 }),
    // @ts-ignore
    getOne: () => Promise.resolve({ data: {} }),
    // @ts-ignore
    update: () => Promise.resolve({ data: {} }),
    updateMany: () => Promise.resolve({}),
};
